import { findDriver } from './drivers'
import { cashSale } from './kassas'
import { linkPhase } from '@krp/client-runtime'
import type { EquipmentSnapshot, HardwareTabId, LinkPhase, VerdictState } from './types'

/**
 * Вердикт «могу ли я продавать» двумя строками (п. 2.2, 2.4): «Чек и наличные» и «Оплата картой».
 * Правило честности: «можно/готов» не пишется без проверенной связи; неизвестное — «не проверялось».
 */

export interface VerdictLineVm {
  id: 'cash' | 'card'
  title: string
  state: VerdictState
  glyph: '✓' | '!' | '✕' | '–' | '?' | '…'
  word: string
  /** Причина или оговорка; слова агента дословно. */
  reason: string | null
  /** Строка целиком, как в макете: «✕ нельзя: смена закрыта — откройте смену в РМК». */
  line: string
  /** Таб, где причину чинят; `null` — вне окна (например, в РМК). */
  target: HardwareTabId | null
}

export interface VerdictVm {
  cash: VerdictLineVm
  card: VerdictLineVm
}

const GLYPHS: Record<VerdictState, VerdictLineVm['glyph']> = {
  ready: '✓',
  warn: '!',
  blocked: '✕',
  'not-configured': '–',
  unknown: '?',
  waiting: '…',
}

const WORDS: Record<VerdictState, string> = {
  ready: 'можно',
  warn: 'можно, но',
  blocked: 'нельзя',
  'not-configured': 'не настроена',
  unknown: 'не проверялось',
  waiting: 'проверяем',
}

function makeLine(id: 'cash' | 'card', title: string, state: VerdictState, reason: string | null, target: HardwareTabId | null): VerdictLineVm {
  const word = WORDS[state]
  const glyph = GLYPHS[state]
  const tail = reason === null || reason.length === 0 ? '' : `: ${reason}`
  return { id, title, state, glyph, word, reason, line: `${glyph} ${word}${tail}`, target }
}

const LINK_REASON: Partial<Record<LinkPhase, string>> = {
  missing: 'нет связи с программой-драйвером',
  lost: 'связь с программой-драйвером потеряна',
  denied: 'программа-драйвер не пускает этот сайт',
  choose: 'выберите программу-драйвер',
}

function cashLine(snapshot: EquipmentSnapshot, phase: LinkPhase): VerdictLineVm {
  if (phase === 'searching') return makeLine('cash', 'Чек и наличные', 'waiting', null, null)
  const linkReason = LINK_REASON[phase]
  if (linkReason !== undefined) return makeLine('cash', 'Чек и наличные', 'blocked', linkReason, 'drivers')
  const sale = cashSale(snapshot)
  return makeLine('cash', 'Чек и наличные', sale.state, sale.reason, sale.target)
}

function cardLine(snapshot: EquipmentSnapshot, phase: LinkPhase): VerdictLineVm {
  const title = 'Оплата картой'
  if (phase === 'searching') return makeLine('card', title, 'waiting', null, null)
  const linkReason = LINK_REASON[phase]
  if (linkReason !== undefined) return makeLine('card', title, 'blocked', linkReason, 'drivers')
  if (phase === 'outdated') {
    return makeLine('card', title, 'unknown', 'программа-драйвер устарела, терминалы не видны — обновите её', 'drivers')
  }
  const data = snapshot.terminals
  if (data === null) return makeLine('card', title, 'waiting', 'проверяем терминалы', null)
  if (data.items.length === 0) return makeLine('card', title, 'not-configured', null, 'terminals')
  const primary = data.items.find((item) => item.active)
  if (primary === undefined) return makeLine('card', title, 'not-configured', 'нет основного терминала', 'terminals')
  const driver = findDriver(snapshot, primary.kind)
  if (driver === null) return makeLine('card', title, 'unknown', 'состояние драйвера терминала неизвестно', 'drivers')
  if (driver.state !== 'ready') {
    return makeLine('card', title, 'blocked', driver.message.length > 0 ? driver.message : 'драйвер терминала не готов', 'drivers')
  }
  const check = snapshot.terminalChecks[primary.id]
  if (check === undefined) return makeLine('card', title, 'unknown', 'связь с терминалом не проверялась', 'terminals')
  if (!check.ok) return makeLine('card', title, 'blocked', check.message, 'terminals')
  return makeLine('card', title, 'ready', null, null)
}

/** Две строки вердикта по снимку. */
export function verdict(snapshot: EquipmentSnapshot): VerdictVm {
  const phase = linkPhase(snapshot.link, snapshot.now)
  return { cash: cashLine(snapshot, phase), card: cardLine(snapshot, phase) }
}
